/**
 * Enterprise / carrier-class cellular routers.
 *
 * Three brands instalators reach for when the install needs SLA-grade
 * cellular: Peplink (SD-WAN bonding + dual-WAN), Cradlepoint (Ericsson-
 * owned, FirstNet / public-safety, vehicle), Sierra Wireless (Semtech,
 * industrial / SCADA / utility metering). All three sit in the
 * `cellular-antenna` category — primary purpose is cellular
 * connectivity even when the unit ships with Wi-Fi or wired LAN.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const ENTERPRISE_CELLULAR_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // Peplink — SD-WAN bonding + mobile-grade 5G
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.peplink.com/products/mobile-routers/max-br1-pro-5g/
  // SRC: https://download.peplink.com/resources/pepwave_br1_pro_5g_datasheet.pdf
  // SRC: https://www.waveform.com/products/peplink-max-br1-pro-5g
  //      MAX BR1 Pro 5G: 5G NSA/SA Sub-6 with 29 5G NR bands (n1, n2,
  //      n3, n5, n7, n8, n12-14, n18, n20, n25-30, n38, n40-42, n46,
  //      n48, n66, n70, n71, n75-79) + 38 LTE bands. 4×4 cellular MIMO
  //      with 4× SMA cellular antenna ports, Wi-Fi 6 2×2 MU-MIMO with
  //      2× Wi-Fi paddle antennas. 2× 2.5 GbE LAN + 1× 2.5 GbE WAN.
  //      Compact metal enclosure 5.8″ × 5.1″ × 1.2″.
  {
    id: 'peplink-max-br1-pro-5g',
    manufacturer: 'Peplink',
    model: 'MAX BR1 Pro 5G',
    category: 'cellular-antenna',
    tagline:
      'MAX BR1 Pro 5G — 5G NSA/SA, 29 NR + 38 LTE bands, 4×4 MIMO, 2× 2.5 GbE, Wi-Fi 6',
    verified: true,
    datasheetUrl: 'https://www.peplink.com/products/mobile-routers/max-br1-pro-5g/',
    specs: {
      technology: '5g-sub6',
      bands:
        '5G NR NSA/SA: n1, n2, n3, n5, n7, n8, n12, n13, n14, n18, n20, n25, n26, n28, n29, n30, n38, n40, n41, n48, n66, n70, n71, n75, n76, n77, n78, n79; LTE: B1, B2, B3, B4, B5, B7, B8, B12, B13, B14, B17, B18, B19, B20, B25, B26, B28, B29, B30, B32, B34, B38, B39, B40, B41, B42, B46, B48, B66, B71',
      mimo: '4×4',
      pattern: 'omni', // 4× SMA cellular + 2× Wi-Fi paddle antennas
      gainDbi: 2,
      simSlots: 1,
      ipRating: 'IP30',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Cradlepoint — Ericsson-owned mobile / FirstNet / public safety
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://cradlepoint.com/product/endpoints/ibr1700/
  // SRC: https://cradlepoint.com/datasheet/ibr1700-series-ruggedized-router/
  // SRC: https://westwardsales.com/cradlepoint-ibr1700
  //      Cradlepoint IBR1700: Cat 18 (1200 Mbps) Gigabit-class LTE-A
  //      Pro modem with secondary modem slot (MC400 add-on for second
  //      carrier), 5× Gigabit Ethernet, simultaneous tri-band Wi-Fi
  //      802.11ac Wave 2 MU-MIMO, active GPS with precision dead-
  //      reckoning, 5G-ready via field-upgradeable modular modem,
  //      ruggedised in-vehicle FirstNet-certified design. Ships with
  //      NetCloud Service for Mobile (cloud mgmt + 24×7 support).
  {
    id: 'cradlepoint-ibr1700',
    manufacturer: 'Cradlepoint',
    model: 'IBR1700',
    category: 'cellular-antenna',
    tagline:
      'IBR1700 — Cat 18 (1200 Mbps) + dual-modem slot, 5× GbE, tri-band Wi-Fi 5, vehicle',
    verified: true,
    datasheetUrl: 'https://cradlepoint.com/datasheet/ibr1700-series-ruggedized-router/',
    specs: {
      technology: 'lte-cat18',
      mimo: '4×4',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2, // primary modem dual-SIM; second modem slot adds more
      ipRating: 'IP64',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Sierra Wireless (Semtech) — industrial / SCADA / metering
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.sierrawireless.com/router-solutions/rv50/
  // SRC: https://5gstore.com/product/7378_sierra_wireless_airlink_raven_rv50x_industrial_lte_advanced_gateway
  // SRC: https://shop.peaktech.com/sierra-wireless-1103052-airlink-raven-rv50x-industrial-lte-advanced-gateway-...
  //      AirLink RV50X: LTE-A Cat 6 (300/50 Mbps), dual SIM with auto-
  //      failover, Ethernet + Serial (RS-232) + USB + GPS, MIL-STD-810G
  //      vibration + Class 1 Division 2 hazardous-area certified,
  //      IP64 ingress protection, <1 W idle (ultra-low-power), up to
  //      5 VPN tunnels. Designed for utility / SCADA / pipeline /
  //      remote-site applications.
  {
    id: 'sierra-airlink-rv50x',
    manufacturer: 'Sierra Wireless',
    model: 'AirLink RV50X',
    category: 'cellular-antenna',
    tagline:
      'AirLink RV50X — Cat 6 industrial gateway, dual SIM, MIL-810G + IP64, <1 W idle',
    verified: true,
    datasheetUrl: 'https://www.sierrawireless.com/router-solutions/rv50/',
    specs: {
      technology: 'lte-cat6',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP64',
      powerConsumptionW: 1, // idle; full operation higher
    },
  },
];
